import { Eye, User, Ban, CheckCircle } from "lucide-react";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { useState, useMemo } from "react";
import { EMPLOYEE_PERSONAL_INFO } from "../../constants/api";
import { checkUserPermissionFunc, numericRecordId } from "../../components/common/CommonFunctions";
import dayjs from "dayjs";
import { cn } from "../../utils/tailwindUtils";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { ConfirmPopupModal } from "../../components/ui/dialogModal";
import useApiMethods from "../../lib/useApiMethods";
import { useIntegrationMappings, IntegratedBadge } from "../integration/integrationBadge";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";

const EmployeeList = () => {
    const { update } = useApiMethods();
    const integrationMap = useIntegrationMappings();
    const { showBranchColumn, insertBranchColumn, TempTransferBadge } = useOrgAdminBranchColumn("employee_name");
    const [employeeData, setEmployeeData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [serverTrigger, setServerTrigger] = useState(false);
    const [actionType, setActionType] = useState("");

    const { edit: editPermission, add: addPermission, active: isActive } = checkUserPermissionFunc("employee", "employees", "SubMenu");
    const navigate = useNavigate();

    const employeeColumns = useMemo(() => insertBranchColumn([
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            width: "250px",
            Cell: ({ row }) => {
                const record = row.original || row;

                const empDetails = {
                    ...record,
                    profile: record?.profile?.length > 0 && record.profile[0] ? record.profile[0] : "",
                    designation_id: record?.designation_id?.length > 0
                        ? { ...record.designation_id[0] }
                        : { id: 0, designation_name: "" },
                    id: record?.employee_id || record?.id || "",
                    employee_id: record?.employee_id || record?.id || "",
                };

                const mapping = integrationMap.get(String(record.id));
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails details={empDetails} size="xs" />
                        <IntegratedBadge mapping={mapping} withLabel={false} />
                        {record?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            },
        },
        {
            accessor: "department",
            Header: "Department",
            sortable: true,
            filterable: true,
            width: "70px",
            Cell: ({ row }) => {
                const record = row.original || row;
                return record?.department_id?.[0]?.department_name || "-";
            },
        },
        {
            accessor: "designation",
            Header: "Designation",
            sortable: true,
            filterable: true,
            width: "70px",
            Cell: ({ row }) => {
                const record = row.original || row;
                return record?.designation_id?.[0]?.designation_name || "-";
            },
        },
        {
            accessor: "reporting_manager_id",
            Header: "Reporting Manager",
            sortable: true,
            filterable: true,
            width: "70px",
            Cell: ({ row }) => {
                const record = row.original || row;

                if (!record?.reporting_manager_id?.length) {
                    return "-";
                }

                const manager = record.reporting_manager_id[0];
                const firstName = manager?.first_name || "";
                const lastName = manager?.last_name || "";

                return `${firstName} ${lastName}`.trim() || "-";
            },
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            Cell: ({ value }) => {
                const active = Boolean(value);

                return (
                    <span
                        className={cn(
                            "px-2 py-1 text-xs font-medium rounded-full capitalize",
                            active
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                        )}
                    >
                        {active ? "Active" : "Inactive"}
                    </span>
                );
            },
        },
        {
            accessor: "date_of_joining",
            Header: "Date of Joining",
            sortable: true,
            filterable: true,
            filterType: "date",
            width: "70px",
            Cell: ({ value }) => {
                if (!value) return "-";

                try {
                    return dayjs(value, "YYYY-MM-DD").format("DD-MM-YYYY");
                } catch (error) {
                    console.error("Date formatting error:", error);
                    return value;
                }
            },
        },
        ...(editPermission ? [
            {
                accessor: "actions",
                Header: "Actions",
                fixed: true,
                align: "right",
                Cell: ({ row }) => {
                    const record = row.original || row;

                    const handleView = () => {
                        navigate("/employee/viewDetails", {
                            state: {
                                row: record,
                                employeeID: numericRecordId(record.id),
                                isActive: isActive,
                            },
                        });
                    };

                    return (
                        <div className="flex gap-1 justify-end">
                            <Button
                                variant="ghost"
                                onClick={handleView}
                                className="px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                                title="View Employee Details"
                            >
                                <Eye className="text-orange-500 h-4 w-4" />
                            </Button>
                        </div>
                    );
                },
            },
        ] : []),
    ]), [editPermission, insertBranchColumn, integrationMap, isActive, navigate, showBranchColumn, TempTransferBadge]);

    const handleAddEmployee = () => {
        navigate("/employee/add");
    };

    const handleConfirmToggle = async () => {
        if (!selectedRow) return;

        setLoading(true);
        try {
            const employeeId = selectedRow.id;
            const newStatus = actionType === "activate";

            const payload = {
                employee_id: employeeId,
                is_active: newStatus,
            };

            await update(EMPLOYEE_PERSONAL_INFO, payload);

            setEmployeeData((prevData) =>
                prevData.map((employee) =>
                    employee.employee_id === employeeId || employee.id === employeeId
                        ? { ...employee, is_active: newStatus }
                        : employee
                )
            );

            setServerTrigger(true);
        } catch (error) {
            console.error(`Error ${actionType}ing employee:`, error);
        } finally {
            setLoading(false);
            setIsConfirmOpen(false);
            setSelectedRow(null);
            setActionType("");
            setServerTrigger(false);
        }
    };

    const getModalConfig = () => {
        const isActivating = actionType === "activate";
        return {
            title: isActivating ? "Activate Employee" : "Deactivate Employee",
            description: isActivating
                ? "Are you sure you want to activate this employee? They will regain access to the system."
                : "Are you sure you want to deactivate this employee? They will no longer have access to the system.",
            confirmlabel: isActivating ? "Activate" : "Deactivate",
            type: isActivating ? "success" : "warning",
        };
    };

    const modalConfig = getModalConfig();

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={User}
                title="Employee"
                subtitle="Manage Employee List and there details"
                buttonEnable={!!addPermission}
                buttonName="Add Employee"
                totalRecords={totalRecords}
                setOpenDialog={handleAddEmployee}
            />

            <Reactable
                data={employeeData}
                setData={setEmployeeData}
                columns={employeeColumns}
                serverSide
                url={EMPLOYEE_PERSONAL_INFO}
                setTotalCount={setTotalRecords}
                serverTrigger={serverTrigger}
                rowSize={editPermission ? "sm" : null}
                isCSVRequired
                isPDFRequired
            />

            <ConfirmPopupModal
                open={isConfirmOpen}
                onClose={() => {
                    setIsConfirmOpen(false);
                    setSelectedRow(null);
                    setActionType("");
                }}
                onConfirm={handleConfirmToggle}
                title={modalConfig.title}
                description={modalConfig.description}
                type={modalConfig.type}
                confirmlabel={modalConfig.confirmlabel}
                destructive={actionType === "deactivate"}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default EmployeeList;
